<template>
  <span class="popper-wrapper">
    <slot></slot>

    <div
      v-if="!targetId"
      v-show="isShown"
      ref="popover"
      class="popper"
      role="tooltip"
    >
      <slot name="popover"></slot>
      <span class="popper__arrow" data-popper-arrow></span>
    </div>
  </span>
</template>

<script>
import { createPopper } from '@popperjs/core';

export default {
  name: 'Popper',
  props: {
    targetId: {
      type: String,
      default: ''
    },
    trigger: {
      type: String,
      default: 'clickToggle'
    },
    placement: {
      type: String,
      default: 'auto'
    },
    appendTo: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      element: null, // the trigger element
      popover: null, // the popover element
      popper: null, // the instance
      isShown: false
    };
  },
  methods: {
    show () {
      this.isShown = true;

      this.create(() => {
        this.$emit('popover:show');
        this.on(document, 'click', this.handleDocumentClick);
      });
    },

    hide () {
      this.isShown = false;
      this.destroy();
      this.popover.removeAttribute('data-show');
      this.off(document, 'click', this.handleDocumentClick);

      this.$emit('popover:hide');
    },

    on (element, event, handler) {
      if (element && event && handler) {
        element.addEventListener(event, handler, false);
      }
    },

    off (element, event, handler) {
      if (element && event) {
        element.removeEventListener(event, handler, false);
      }
    },

    elementContains (parent, child) {
      if (typeof parent.contains === 'function') {
        return parent.contains(child);
      }

      return false;
    },

    handleClickToggle (e) {
      e.preventDefault();
      e.stopPropagation();

      if (this.isShown) {
        this.hide();
      } else {
        this.$root.$emit('openFilterSpecific', this.targetId);
        this.show();
      }
    },

    handleDocumentClick (e) {
      if (this.popper && this.isShown && (e.target === this.popover || this.elementContains(this.popover, e.target))) {
        return;
      }

      this.hide();
    },

    create (callback) {
      this.popper = createPopper(this.element, this.popover, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 14]
            }
          }
        ],
        onFirstUpdate: () => {
          callback();
        }
      });
    },

    destroy () {
      if (this.popper) {
        this.popper.destroy();
        this.popper = null;
      }
    }
  },
  mounted () {
    /* Define trigger and popover elements to pass to the Popper.js lib */
    this.element = this.$slots.default[0].elm;

    this.popover = (() => {
      if (this.targetId) {
        return (document.getElementById(this.targetId));
      }

      return this.$refs.popover;
    })();

    /* Move the popover to the body if the option is set and add an attribute */
    if (this.appendTo === 'body') {
      this.popover.parentNode.removeChild(this.popover);
      this.popover.setAttribute('data-popper-outplaced', true);
      document.querySelector('body').appendChild(this.popover);
    }

    /* Assign classes */
    if (this.element.className) {
      this.$el.setAttribute('data-element-class', this.$slots.default[0].elm.className);
    }

    switch (this.trigger) {
      // Shall we rename the case's names? they are not really self explanatory :/
      case 'clickToOpen':
        this.on(this.element, 'click', this.handleClickToggle);
        this.on(document, 'click', this.hide);
        break;

      case 'clickToggle':
        this.on(this.element, 'click', this.handleClickToggle);
        break;

      case 'hover':
        this.on(this.element, 'mouseenter', this.show);
        this.on(this.popover, 'mouseenter', this.show);
        this.on(this.element, 'mouseleave', this.hide);
        this.on(this.popover, 'mouseleave', this.hide);
        break;

      case 'focus':
        this.on(this.element, 'focus', this.show);
        this.on(this.popover, 'focus', this.show);
        this.on(this.element, 'blur', this.hide);
        this.on(this.popover, 'blur', this.hide);
        break;

      case 'onLoad':
        // 🟠 TODO: Remove the timeout function in order to show the popper
        setTimeout(() => {
          this.show();
          this.on(document, 'click', this.hide);
        }, 20);
        break;
    };

    this.$root.$on('openFilterSpecific', (openId) => {
      if (openId !== this.targetId) {
        this.hide();
      }
    });
  },
  beforeDestroy () {
    this.hide();
    this.off(document, 'click', this.handleDocumentClick);

    switch (this.trigger) {
      case 'clickToOpen':
        this.off(this.element, 'click', this.hide);
        break;

      case 'clickToggle':
        this.off(this.element, 'click', this.hide);
        break;

      case 'hover':
        this.off(this.element, 'mouseenter', this.hide);
        this.off(this.element, 'mouseleave', this.hide);
        this.off(this.popover, 'mouseenter', this.hide);
        this.off(this.popover, 'mouseleave', this.hide);
        break;

      case 'focus':
        this.off(this.element, 'focus', this.hide);
        this.off(this.element, 'blur', this.hide);
        this.off(this.popover, 'focus', this.hide);
        this.off(this.popover, 'blur', this.hide);
        break;

      case 'onLoad':
        this.hide();
        this.off(this.element, 'click', this.hide);
        break;
    }
  }
};
</script>

<style lang="scss" scoped>
/**
 * Popover
 *
 * @author juanpablob <m.juanpablob@gmail.com>
 * @desc Tooltip and popover component based on Popper.js
 * @since 2020-01-05
**/

$component: 'popper';
$popper-distance: 14px;

.#{$component} {
  display: inline-block;
  position: absolute;
  width: auto;
  @include rfs(28rem, max-width);
  @include padding(map-get($spacers, 3));
  border-radius: $border-radius-lg;
  background-color: $white;
  box-shadow: 0 0 0 1px rgba(63, 63, 68, 0.05),
              -0.2rem 0.3rem 0.3rem 0.1rem rgba($ink, 0.1);
  z-index: 30;
  opacity: 0;
  transition: opacity 0.1s ease-in;
  color: $ink;
  font-family: $font-family-sans-serif;
  @include font-size($font-size-base);

  &__arrow,
  &__arrow:after {
    position: absolute;
    width: 14px;
    height: 14px;
  }

  &__arrow {
    position: relative;
    filter: drop-shadow(0 2px 1px rgba(63, 63, 68, 0.080));
    z-index: 0;
  }

  &__arrow:after {
    content: '';
    background: $white;
    transform: rotate(45deg);
    z-index: -1;
  }

  &[data-popper-placement] {
    opacity: 100;
  }

  &[data-popper-placement^='top'] > .popper__arrow {
    bottom: -6px;
  }

  &[data-popper-placement^='right'] > .popper__arrow {
    left: -6px;
    filter: drop-shadow(-2px 0 1px rgba(63, 63, 68, 0.080));
  }

  &[data-popper-placement^='left'] > .popper__arrow {
    right: -6px;
    filter: drop-shadow(2px 0 1px rgba(63, 63, 68, 0.080));
  }

  &[data-popper-placement^='bottom'] > .popper__arrow {
    top: -6px;
    filter: drop-shadow(0 -2px 1px rgba(63, 63, 68, 0.080));
  }
}
</style>
